import React, { useState } from "react";
import { PieChart, Cell, Pie, Tooltip, Label, ResponsiveContainer } from "recharts";
import UseGetDashBoardWithParams from "../../../../../Components/Hooks/useDashboardDataParams";
import capitalizeFirstLetter from "../../../../../Components/Utils/Funcs";
import { useNavigate } from "react-router-dom";

export default function MonthWiseProcurment() {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const navigate = useNavigate();

  const [params, setParams] = useState({
    month: currentMonth,
    year: currentYear,
  });
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const { data, loading } = UseGetDashBoardWithParams(`/new-dashboard/procurement/ypr-received`, params);

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setParams({ ...params, month: newMonth });
    setSelectedMonth(newMonth);
  };

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setParams({ ...params, year: newYear });
    setSelectedYear(newYear);
  };

  const months = [
    { name: "Jan", value: 1 },
    { name: "Feb", value: 2 },
    { name: "Mar", value: 3 },
    { name: "Apr", value: 4 },
    { name: "May", value: 5 },
    { name: "Jun", value: 6 },
    { name: "Jul", value: 7 },
    { name: "Aug", value: 8 },
    { name: "Sep", value: 9 },
    { name: "Oct", value: 10 },
    { name: "Nov", value: 11 },
    { name: "Dec", value: 12 },
  ];

  const salesmenData =
    data?.salesmen?.map((salesman) => ({
      name: salesman.name,
      catA: salesman["Category A"] || 0,
      catB: salesman["Category B"] || 0,
      catC: salesman["Category C"] || 0,
      total: salesman.total || 0,
    })) || [];

  const COLORS = ["#6366F1", "#EC4899", "#F59E0B"];

  const chartData = [
    { name: "Category A", value: data?.customers?.["Category A"] || 0, label: "A" },
    { name: "Category B", value: data?.customers?.["Category B"] || 0, label: "B" },
    { name: "Category C", value: data?.customers?.["Category C"] || 0, label: "C" },
  ].filter((entry) => entry.value > 0);

  const handleSliceClick = (label) => {
    const path = "/procurement/purchase-requests";
    navigate(path, { state: { category: label } });
  };

  return (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_25px_rgba(0,0,0,0.04)] transition-all duration-300 flex flex-col justify-between">
      <div className="flex justify-between items-center pb-3 border-b border-gray-100 mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">YPR Assigned to Buyers</h3>
        </div>

        <div className="flex space-x-1.5">
          <select
            value={selectedYear}
            onChange={handleYearChange}
            className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-xs font-medium text-gray-700 outline-none"
          >
            {[...Array(10)].map((_, index) => (
              <option key={index} value={currentYear - 5 + index}>
                {currentYear - 5 + index}
              </option>
            ))}
          </select>
          <select
            value={selectedMonth}
            onChange={handleMonthChange}
            className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-xs font-medium text-gray-700 outline-none"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="h-52 flex items-center justify-center animate-pulse">
          <div className="h-36 bg-gray-100 rounded-xl w-full"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center flex-1">
          {/* Donut Chart */}
          <div className="md:col-span-5 relative flex justify-center items-center h-44">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={38}
                    outerRadius={58}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => handleSliceClick(e.label)}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                    <Label
                      value={`${data?.customers?.total || "0"}`}
                      position="center"
                      style={{ fontSize: "15px", fontWeight: "bold", fill: "#1F2937" }}
                    />
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1F2937",
                      borderRadius: "8px",
                      color: "#FFF",
                      border: "none",
                      fontSize: "11px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-gray-400 text-xs font-medium">No YPR data</div>
            )}
          </div>

          {/* Buyer Breakdown Table */}
          <div className="md:col-span-7 max-h-48 overflow-y-auto pr-1">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-gray-500 font-semibold border-b border-gray-200">
                <tr>
                  <th className="py-1.5 px-2 text-left">Buyer</th>
                  <th className="py-1.5 px-1 text-center">Cat A</th>
                  <th className="py-1.5 px-1 text-center">Cat B</th>
                  <th className="py-1.5 px-1 text-center">Cat C</th>
                  <th className="py-1.5 px-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {salesmenData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50/70 transition-colors">
                    <td className="py-2 px-2 font-medium text-gray-800 truncate max-w-[90px]">
                      {capitalizeFirstLetter(item.name || "N/A")}
                    </td>
                    <td className="py-2 px-1 text-center text-indigo-600 font-semibold">{item.catA}</td>
                    <td className="py-2 px-1 text-center text-pink-600 font-semibold">{item.catB}</td>
                    <td className="py-2 px-1 text-center text-amber-600 font-semibold">{item.catC}</td>
                    <td className="py-2 px-2 text-right font-bold text-gray-900">{item.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
